import { useEffect, useState } from 'react';
import Select from 'react-select';
import { DateRange } from './dateRange';
import {
  InputText,
  LighterGreenButton,
  SecondaryButton,
  Select as NativeSelect,
} from '../ui/Inputs';
import { usePortalStore } from '../../stores/portalStore';
import { formatDate } from '../../utils/date';
import { AiOutlineCloseCircle } from 'react-icons/ai';
import { BiSearch } from 'react-icons/bi';
import { FiChevronDown, FiChevronUp } from 'react-icons/fi';
import { getDistributionCenterName } from '../../stores/catalog/DistributionCenters';

export const FilterValueType = { DATE: 'date' };
export const FilterOperations = {
  EQUAL: 'Igual à',
  CONTAINS: 'Contém',
  STARTS_WITH: 'Começa com',
  DIFFERENT: 'Diferente de',
  BETWEEN: 'Entre',
};

// ─── Normaliza string removendo tudo que não é letra ou dígito ────────────────
const normalizeForSearch = (str) =>
  String(str ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');

// ─── filterOption customizado para Empresa/CNPJ ───────────────────────────────
// Permite busca com ou sem formatação: "12345678", "12.345", "Empresa A", etc.
const empresaCnpjFilterOption = (option, inputValue) => {
  if (!inputValue) return true;
  const normalizedInput = normalizeForSearch(inputValue);
  const normalizedLabel = normalizeForSearch(option.label);
  return normalizedLabel.includes(normalizedInput);
};

// ─── Highlight do termo buscado ───────────────────────────────────────────────
const highlightMatch = (label, inputValue) => {
  if (!inputValue) return label;

  const normalizedInput = normalizeForSearch(inputValue);
  const normalizedLabel = normalizeForSearch(label);
  const matchIndex = normalizedLabel.indexOf(normalizedInput);

  if (matchIndex === -1) {
    const regex = new RegExp(`(${inputValue})`, 'gi');
    const parts = String(label).split(regex);
    return parts.map((part, i) =>
      regex.test(part) ? (
        <mark
          key={i}
          style={{
            background: '#d1fae5',
            color: '#065f46',
            borderRadius: '2px',
            padding: '0 1px',
          }}
        >
          {part}
        </mark>
      ) : (
        part
      )
    );
  }

  let normPos = 0;
  let origStart = -1;
  let origEnd = -1;
  for (let i = 0; i < label.length; i++) {
    const ch = label[i].toLowerCase().replace(/[^a-z0-9]/g, '');
    if (ch !== '') {
      if (normPos === matchIndex) origStart = i;
      if (normPos === matchIndex + normalizedInput.length - 1) {
        origEnd = i + 1;
        break;
      }
      normPos++;
    }
  }

  if (origStart === -1 || origEnd === -1) return label;

  return (
    <>
      {label.slice(0, origStart)}
      <mark
        style={{
          background: '#d1fae5',
          color: '#065f46',
          borderRadius: '2px',
          padding: '0 1px',
        }}
      >
        {label.slice(origStart, origEnd)}
      </mark>
      {label.slice(origEnd)}
    </>
  );
};

// ─── Estilos compartilhados ───────────────────────────────────────────────────
const SHARED_CONTROL = {
  height: '36px',
  borderRadius: '6px',
  fontSize: '13px',
  borderColor: '#cbd5e0',
  boxSizing: 'border-box',
};

const SELECT_MAX_WIDTH = 420;

const buildSelectStyles = (minWidth) => ({
  control: (base, state) => ({
    ...base,
    minHeight: '36px',
    borderRadius: SHARED_CONTROL.borderRadius,
    fontSize: SHARED_CONTROL.fontSize,
    boxSizing: 'border-box',
    minWidth,
    maxWidth: `${SELECT_MAX_WIDTH}px`,
    flexWrap: 'wrap',
    borderColor: state.isFocused ? '#4a9e55' : SHARED_CONTROL.borderColor,
    boxShadow: state.isFocused ? '0 0 0 2px rgba(74,158,85,0.2)' : 'none',
    '&:hover': { borderColor: '#4a9e55' },
  }),
  valueContainer: (base) => ({
    ...base,
    padding: '4px 8px',
    gap: '4px',
    maxHeight: '96px',
    overflowY: 'auto',
  }),
  input: (base) => ({ ...base, margin: 0, padding: 0 }),
  indicatorsContainer: (base) => ({
    ...base,
    alignSelf: 'flex-start',
    height: '36px',
  }),
  indicatorSeparator: () => ({ display: 'none' }),
  dropdownIndicator: (base) => ({ ...base, padding: '0 6px' }),
  menu: (base) => ({ ...base, fontSize: '13px', zIndex: 99 }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? '#4a9e55'
      : state.isFocused
      ? '#f0fdf4'
      : 'white',
    color: state.isSelected ? 'white' : 'inherit',
    padding: '7px 12px',
  }),
  placeholder: (base) => ({
    ...base,
    color: '#a0aec0',
    fontSize: '13px',
    whiteSpace: 'nowrap',
  }),
  multiValue: (base) => ({
    ...base,
    backgroundColor: '#e6f4ea',
    borderRadius: '12px',
    overflow: 'hidden',
    margin: 0,
  }),
  multiValueLabel: (base) => ({
    ...base,
    color: '#1f6d2c',
    fontSize: '12px',
    padding: '3px 4px 3px 8px',
  }),
  multiValueRemove: (base) => ({
    ...base,
    color: '#1f6d2c',
    borderRadius: '0 12px 12px 0',
    ':hover': { backgroundColor: '#c8e6c9', color: '#14532d' },
  }),
});

const CHAR_WIDTH = 7.5;
const PADDING = 70;

const minWidthFromPlaceholder = (text) =>
  `${Math.min(
    Math.ceil(text.length * CHAR_WIDTH + PADDING),
    SELECT_MAX_WIDTH
  )}px`;

// ─── Nomes de campo que ativam o filterOption normalizado ─────────────────────
// Inclui tanto 'Empresa/CNPJ' (legado) quanto 'Empresa - CNPJ' (novo padrão)
const EMPRESA_CNPJ_FIELD_NAMES = new Set(['Empresa/CNPJ', 'Empresa - CNPJ']);

// ─── Componente ───────────────────────────────────────────────────────────────
export const BasicFilter = (props) => {
  const { fieldList, onSearch, leftSlot, rightSlot } = props;
  const currentUser = usePortalStore((state) => state.currentUser);

  const [selectedFilters, setSelectedFilters] = useState([]);
  const [expandedGroups, setExpandedGroups] = useState(() => new Set());
  const [, setCurrentConfigureFilter] = useState([]);
  const [currentFieldFilter, setCurrentFieldFilter] = useState('');
  const [currentFieldOperationFilter, setCurrentFieldOperationFilter] =
    useState('');
  const [currentValue, setCurrentValue] = useState('');
  const [availableValues, setAvailableValues] = useState([]);
  const [fields, setFields] = useState([]);
  const [filterObjs, setFilterObjs] = useState([]);
  const [maxDate, setMaxDate] = useState();

  const hasSelectableValues =
    Array.isArray(availableValues) && availableValues.length > 0;

  const placeholder = currentFieldFilter
    ? `Buscar ${currentFieldFilter}...`
    : 'Digite';
  const inputMinWidth = minWidthFromPlaceholder(placeholder);

  // Ativa busca normalizada para campos de Empresa/CNPJ (qualquer variante do nome)
  const isEmpresaCnpjField = EMPRESA_CNPJ_FIELD_NAMES.has(currentFieldFilter);

  const selectOptions = availableValues.map((v) => ({
    value: v,
    label:
      currentFieldFilter === 'Filial Distribuidora'
        ? getDistributionCenterName(v)
        : String(v),
  }));

  const currentSelectValues = Array.isArray(currentValue)
    ? currentValue
    : currentValue
    ? [currentValue]
    : [];

  const setFilter = (filter) => {
    setCurrentFieldFilter(filter);
    const filterObj = filterObjs.find((x) => x.name === filter);

    if (filterObj) {
      if (filterObj.name === 'Paciente') {
        setCurrentFieldOperationFilter(FilterOperations.CONTAINS);
      } else if (['Data', 'Estimativa de entrega'].includes(filterObj.name)) {
        setCurrentFieldOperationFilter(FilterOperations.BETWEEN);
        setMaxDate(
          filterObj.name === 'Estimativa de entrega' ? undefined : new Date()
        );
      } else {
        setCurrentFieldOperationFilter(FilterOperations.EQUAL);
      }
      setCurrentConfigureFilter(filterObj.operations);
      setAvailableValues(
        Array.isArray(filterObj.values) ? [...filterObj.values] : []
      );
      setCurrentValue(
        Array.isArray(filterObj.values) && filterObj.values.length > 0 ? [] : ''
      );
    } else {
      setCurrentValue('');
    }
  };

  const appendCurrentFilter = () => {
    if (!currentFieldOperationFilter) return selectedFilters;

    const valuesToAdd = hasSelectableValues
      ? currentSelectValues.filter((value) => String(value ?? '').trim() !== '')
      : [currentValue].filter((value) => String(value ?? '').trim() !== '');
    if (valuesToAdd.length === 0) return selectedFilters;

    const next = [...selectedFilters];
    valuesToAdd.forEach((value) => {
      const newFilter = {
        name: currentFieldFilter,
        operation: currentFieldOperationFilter,
        value,
      };
      const alreadyExists = next.some(
        (x) =>
          x.name === newFilter.name &&
          x.operation === newFilter.operation &&
          normalizeForSearch(JSON.stringify(x.value)) ===
            normalizeForSearch(JSON.stringify(newFilter.value))
      );
      if (!alreadyExists) next.push(newFilter);
    });
    return next;
  };

  const resetCurrentFilter = () => {
    setCurrentFieldFilter('');
    setCurrentFieldOperationFilter('');
    setCurrentConfigureFilter([]);
    setCurrentValue([]);
    setAvailableValues([]);
  };

  const addFilter = () => {
    const next = appendCurrentFilter();
    setSelectedFilters(next);
    resetCurrentFilter();
  };

  const applyFilters = () => {
    const next = appendCurrentFilter();
    setSelectedFilters(next);
    resetCurrentFilter();
    if (typeof onSearch === 'function') onSearch(next);
  };

  const removeFilter = (idx) => {
    const next = selectedFilters.filter((_, i) => i !== idx);
    setSelectedFilters(next);
    if (typeof onSearch === 'function') onSearch(next);
  };

  const removeGroup = (name) => {
    const next = selectedFilters.filter((filter) => filter.name !== name);
    setSelectedFilters(next);
    if (typeof onSearch === 'function') onSearch(next);
  };

  const toggleGroup = (name) => {
    setExpandedGroups((previous) => {
      const next = new Set(previous);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const cleanAll = () => {
    setSelectedFilters([]);
    setCurrentValue(hasSelectableValues ? [] : '');
    if (typeof onSearch === 'function') onSearch([]);
  };

  const onEnterTyped = (e) => {
    if (e.key === 'Enter' || e.keyCode === 13) addFilter();
  };

  const printFormat = (value) => {
    if (Array.isArray(value))
      return `${formatDate(value[0])} - ${formatDate(value[1])}`;
    return value;
  };

  const formatFilterValue = (filter) =>
    filter.name === 'Filial Distribuidora'
      ? printFormat(getDistributionCenterName(filter.value))
      : printFormat(filter.value);

  useEffect(() => {
    let filterToFill = [...fieldList];
    if (!currentUser.canSeeFullOrdersTable) {
      filterToFill = filterToFill.filter(
        (f) => f.name !== 'CNPJ' && f.name !== 'Filial Distribuidora'
        // 'Empresa - CNPJ' e 'Empresa/CNPJ' não são removidos:
        // usuários comuns devem ver o filtro de empresa (com seus próprios CNPJs)
      );
    }
    setFilterObjs(filterToFill);
    setFields(filterToFill.map((x) => x.name));
  }, [fieldList]);

  const groupedFilters = [];
  const groupsByName = new Map();
  selectedFilters.forEach((filter, idx) => {
    if (!groupsByName.has(filter.name)) {
      const group = { name: filter.name, items: [] };
      groupsByName.set(filter.name, group);
      groupedFilters.push(group);
    }
    groupsByName.get(filter.name).items.push({ ...filter, idx });
  });

  return (
    <div className="filter-content">
      <div className="filter-inputs-row">
        {leftSlot && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              height: '36px',
              marginRight: '10px',
            }}
          >
            {leftSlot}
          </div>
        )}

        <div className="filter-title">Filtrar por</div>

        <div className="filter-custom">
          {/* Campo */}
          <div className="filter-inputs">
            <NativeSelect
              className="combobox-custom"
              style={{ lineHeight: '30px', height: '36px' }}
              value={currentFieldFilter}
              onChange={(e) => setFilter(e.target.value)}
            >
              <option value="">Selecione um campo</option>
              {fields.map((x, idx) => (
                <option key={idx} value={x}>
                  {x}
                </option>
              ))}
            </NativeSelect>
          </div>

          {/* Valor */}
          <div className="filter-inputs">
            {currentFieldOperationFilter === FilterOperations.BETWEEN ? (
              <div style={{ minWidth: '250px' }}>
                <DateRange
                  maxDate={maxDate}
                  selectRange={true}
                  height="36px"
                  onSelect={(dates) => setCurrentValue(dates)}
                />
              </div>
            ) : hasSelectableValues ? (
              <Select
                options={selectOptions}
                value={selectOptions.filter((option) =>
                  currentSelectValues.includes(option.value)
                )}
                onChange={(options) =>
                  setCurrentValue(
                    Array.isArray(options)
                      ? options.map((option) => option.value)
                      : []
                  )
                }
                styles={buildSelectStyles(inputMinWidth)}
                placeholder={placeholder}
                isMulti
                isClearable
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                filterOption={
                  isEmpresaCnpjField ? empresaCnpjFilterOption : undefined
                }
                noOptionsMessage={({ inputValue }) =>
                  inputValue
                    ? `Nenhum resultado para "${inputValue}"`
                    : 'Sem opções'
                }
                formatOptionLabel={(option, { inputValue }) => (
                  <span>{highlightMatch(option.label, inputValue)}</span>
                )}
              />
            ) : (
              <InputText
                style={{
                  ...SHARED_CONTROL,
                  minWidth: inputMinWidth,
                  padding: '0 8px',
                  outline: 'none',
                  transition: 'border-color 0.2s, box-shadow 0.2s',
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#4a9e55';
                  e.target.style.boxShadow = '0 0 0 2px rgba(74,158,85,0.2)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#cbd5e0';
                  e.target.style.boxShadow = 'none';
                }}
                onKeyUp={onEnterTyped}
                value={currentValue}
                placeholder={placeholder}
                onChange={(e) => setCurrentValue(e.target.value)}
              />
            )}
          </div>

          <div
            className="filter-buttons"
            style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
          >
            <div>
              <LighterGreenButton
                style={{
                  boxSizing: 'border-box',
                  height: '36px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                onClick={applyFilters}
              >
                <BiSearch
                  style={{ fontSize: '20px', color: '#fff', display: 'block' }}
                />
              </LighterGreenButton>
            </div>
            <div>
              <SecondaryButton
                style={{ boxSizing: 'border-box', height: '36px' }}
                onClick={addFilter}
              >
                Adicionar
              </SecondaryButton>
            </div>
            <div>
              <SecondaryButton
                style={{ boxSizing: 'border-box', height: '36px' }}
                onClick={cleanAll}
              >
                Limpar todos
              </SecondaryButton>
            </div>
          </div>
        </div>

        {rightSlot && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              height: '36px',
              marginLeft: 'auto',
            }}
          >
            {rightSlot}
          </div>
        )}
      </div>

      {/* Chips */}
      <div className="chips-panel">
        {groupedFilters.map((group) => {
          const isGroup = group.items.length > 1;
          const isExpanded = expandedGroups.has(group.name);
          const headerValue = isGroup
            ? `${group.items.length} selecionados`
            : formatFilterValue(group.items[0]);

          return (
            <div key={group.name} className="filter-chip-group">
              <div className="filter-chips">
                <div className="chip">{group.name}: </div>
                <div className="chip chip-value" title={String(headerValue)}>
                  {headerValue}
                </div>
                {isGroup && (
                  <div
                    className="chip-expand"
                    style={{ cursor: 'pointer' }}
                    title={
                      isExpanded
                        ? 'Recolher'
                        : `Ver ${group.items.length} valores`
                    }
                    onClick={() => toggleGroup(group.name)}
                  >
                    {isExpanded ? (
                      <FiChevronUp size={14} />
                    ) : (
                      <FiChevronDown size={14} />
                    )}
                  </div>
                )}
                <div
                  className="chip-del"
                  style={{ cursor: 'pointer', fontWeight: '600' }}
                  onClick={() => removeGroup(group.name)}
                >
                  <AiOutlineCloseCircle
                    style={{
                      fontSize: '17px',
                      cursor: 'pointer',
                      marginLeft: '10px',
                    }}
                  />
                </div>
              </div>
              {isGroup && isExpanded && (
                <div className="filter-chip-extra">
                  {group.items.map((item) => (
                    <div key={item.idx} className="filter-chip-extra-item">
                      <span title={String(formatFilterValue(item))}>
                        {formatFilterValue(item)}
                      </span>
                      <div
                        className="chip-del"
                        style={{ cursor: 'pointer', fontWeight: '600' }}
                        onClick={() => removeFilter(item.idx)}
                      >
                        <AiOutlineCloseCircle
                          style={{ fontSize: '15px', cursor: 'pointer' }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
